import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

export const PALETTE = ["#f59e0b", "#2563eb", "#7c3aed", "#ec4899", "#10b981", "#ef4444", "#06b6d4", "#84cc16", "#a16207", "#9333ea", "#f97316", "#0ea5e9", "#be123c", "#15803d"];
const S = 0.01;
const over = (a, b, x, y) => x < b.x + b.l && b.x < x + a.l && y < b.y + b.w && b.y < y + a.w;
export const restZ = (pl, i, x, y) => pl.reduce((m, q, k) => (k !== i && over(pl[i], q, x, y) ? Math.max(m, q.z + q.h) : m), 0);

const labelTex = (color, text) => {
  const c = document.createElement("canvas"); c.width = 256; c.height = 128;
  const g = c.getContext("2d");
  g.fillStyle = color; g.fillRect(0, 0, 256, 128);
  g.strokeStyle = "rgba(255,255,255,0.55)"; g.lineWidth = 6; g.strokeRect(3, 3, 250, 122);
  g.fillStyle = "#fff"; g.textAlign = "center"; g.textBaseline = "middle";
  const txt = String(text);
  let fs = 64; g.font = `800 ${fs}px sans-serif`;
  while (fs > 26 && g.measureText(txt).width > 232) { fs -= 2; g.font = `800 ${fs}px sans-serif`; }
  g.shadowColor = "rgba(0,0,0,.45)"; g.shadowBlur = 4; g.shadowOffsetY = 2;
  g.fillText(txt, 128, 68);
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
};

const Tir3D = forwardRef(function Tir3D({ L, W, H, placements, colorOf, labelOf, selected, onSelect, onMove, locked = false, height = 460 }, ref) {
  const box = useRef(null);
  const st = useRef({});

  useEffect(() => {
    const el = box.current;
    const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    renderer.setSize(el.clientWidth, height);
    el.appendChild(renderer.domElement);
    const scene = new THREE.Scene(); scene.background = new THREE.Color("#f1f5f9");
    const camera = new THREE.PerspectiveCamera(40, el.clientWidth / height, 0.1, 200);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    scene.add(new THREE.AmbientLight(0xffffff, 0.75));
    const dl = new THREE.DirectionalLight(0xffffff, 0.9); dl.position.set(-8, 14, 10); scene.add(dl);
    const group = new THREE.Group(); scene.add(group);
    const frame = new THREE.Group(); scene.add(frame);
    let raf; const loop = () => { controls.update(); renderer.render(scene, camera); raf = requestAnimationFrame(loop); }; loop();
    const ro = new ResizeObserver(() => { const w = el.clientWidth; renderer.setSize(w, height); camera.aspect = w / height; camera.updateProjectionMatrix(); });
    ro.observe(el);
    let down = null, drag = null;
    const rc = new THREE.Raycaster();
    const ray = (e) => { const r = renderer.domElement.getBoundingClientRect(); rc.setFromCamera(new THREE.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1), camera); };
    const snap = (v) => Math.round(v / 5) * 5;
    const pd = (e) => {
      down = [e.clientX, e.clientY];
      ray(e);
      const hit = rc.intersectObjects(group.children, false)[0];
      if (!hit || !st.current.onMove) return;
      e.stopImmediatePropagation();
      const idx = hit.object.userData.idx; const p = st.current.placements[idx];
      st.current.onSelect?.(idx);
      const vertical = false;
      const normal = vertical ? new THREE.Vector3(camera.position.x - hit.point.x, 0, camera.position.z - hit.point.z).normalize() : new THREE.Vector3(0, 1, 0);
      const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, hit.point);
      drag = { idx, p, plane, start: hit.point.clone(), mesh: hit.object, vertical, moved: false, nx: p.x, ny: p.y, nz: p.z };
      controls.enabled = false;
      renderer.domElement.setPointerCapture(e.pointerId);
    };
    const pm = (e) => {
      if (!drag) return;
      ray(e);
      const pt = new THREE.Vector3();
      if (!rc.ray.intersectPlane(drag.plane, pt)) return;
      const d = pt.sub(drag.start); const { L: l0, W: w0, H: h0 } = st.current.dims; const p = drag.p;
      if (drag.vertical) drag.nz = Math.max(0, Math.min(h0 - p.h, snap(p.z + d.y / S)));
      else {
        drag.nx = Math.max(0, Math.min(l0 - p.l, snap(p.x + d.x / S))); drag.ny = Math.max(0, Math.min(w0 - p.w, snap(p.y + d.z / S)));
        drag.nz = Math.min(Math.max(0, h0 - p.h), restZ(st.current.placements, drag.idx, drag.nx, drag.ny));
      }
      drag.moved = true;
      drag.mesh.position.set((drag.nx + p.l / 2 - l0 / 2) * S, (drag.nz + p.h / 2) * S, (drag.ny + p.w / 2 - w0 / 2) * S);
    };
    const pu = (e) => {
      controls.enabled = true;
      if (drag) {
        const d = drag; drag = null;
        if (d.moved) { const { L: l0, W: w0 } = st.current.dims; const p = d.p; d.mesh.position.set((p.x + p.l / 2 - l0 / 2) * S, (p.z + p.h / 2) * S, (p.y + p.w / 2 - w0 / 2) * S); st.current.onMove?.(d.idx, { x: d.nx, y: d.ny }); }
        return;
      }
      if (!down || Math.hypot(e.clientX - down[0], e.clientY - down[1]) > 5) return;
      ray(e);
      const hit = rc.intersectObjects(group.children, false)[0];
      st.current.onSelect?.(hit ? hit.object.userData.idx : null);
    };
    renderer.domElement.addEventListener("pointerdown", pd, true);
    renderer.domElement.addEventListener("pointermove", pm);
    renderer.domElement.addEventListener("pointerup", pu);
    st.current = { ...st.current, renderer, scene, camera, controls, group, frame };
    return () => { cancelAnimationFrame(raf); ro.disconnect(); controls.dispose(); renderer.dispose(); el.innerHTML = ""; };
  }, [height]);

  useEffect(() => { st.current.onSelect = onSelect; st.current.onMove = onMove; st.current.placements = placements; st.current.dims = { L, W, H }; }, [onSelect, onMove, placements, L, W, H]);

  useEffect(() => {
    const { frame, camera, controls } = st.current;
    if (!frame) return;
    frame.clear();
    const g = new THREE.BoxGeometry(L * S, H * S, W * S);
    const edges = new THREE.LineSegments(new THREE.EdgesGeometry(g), new THREE.LineBasicMaterial({ color: "#ef4444" }));
    edges.position.y = (H * S) / 2; frame.add(edges);
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(L * S, W * S), new THREE.MeshLambertMaterial({ color: "#cbd5e1" }));
    floor.rotation.x = -Math.PI / 2; floor.position.y = 0.001; frame.add(floor);
    const cab = new THREE.Mesh(new THREE.BoxGeometry(1.9, 2.6, W * S * 0.95), new THREE.MeshLambertMaterial({ color: "#e2e8f0", transparent: true, opacity: 0.55 }));
    cab.position.set(-(L * S) / 2 - 1.1, 1.3 + 0.4, 0); frame.add(cab);
    const k = Math.max(L, W * 3) * S;
    camera.position.set(-k * 0.55, k * 0.55, k * 0.75); controls.target.set(0, (H * S) / 3, 0); controls.update();
  }, [L, W, H]);

  useEffect(() => {
    const { group } = st.current;
    if (!group) return;
    group.children.forEach((c) => c.geometry?.dispose());
    group.clear();
    const geos = {}, mats = {};
    const line = new THREE.LineBasicMaterial({ color: "#1e293b", transparent: true, opacity: 0.35 });
    const selMat = new THREE.MeshLambertMaterial({ color: "#facc15", emissive: "#a16207" });
    placements.forEach((p, idx) => {
      const rulo = p.sekil === "RULO";
      const gk = `${rulo ? "R" : "B"}${p.l}|${p.w}|${p.h}|${p.rulo_yon}`;
      if (!geos[gk]) {
        if (!rulo) geos[gk] = new THREE.BoxGeometry(p.l * S, p.h * S, p.w * S);
        else {
          const vertical = p.rulo_yon !== "YATAY";
          const along = vertical ? p.h : Math.max(p.l, p.w);
          const r = (vertical ? Math.min(p.l, p.w) : Math.min(p.h, Math.min(p.l, p.w))) / 2;
          const c = new THREE.CylinderGeometry(r * S, r * S, along * S, 24);
          if (!vertical) { if (p.l >= p.w) c.rotateZ(Math.PI / 2); else c.rotateX(Math.PI / 2); }
          geos[gk] = c;
        }
      }
      if (!mats[p.pid]) mats[p.pid] = new THREE.MeshLambertMaterial({ color: "#ffffff", map: labelTex(colorOf(p.pid), labelOf(p.pid)) });
      const mesh = new THREE.Mesh(geos[gk], idx === selected ? selMat : mats[p.pid]);
      mesh.position.set((p.x + p.l / 2 - L / 2) * S, (p.z + p.h / 2) * S, (p.y + p.w / 2 - W / 2) * S);
      mesh.userData.idx = idx;
      if (placements.length < 1500) mesh.add(new THREE.LineSegments(new THREE.EdgesGeometry(geos[gk]), line));
      group.add(mesh);
    });
  }, [placements, selected, L, W, H, colorOf, labelOf]);

  useEffect(() => { if (st.current.controls) st.current.controls.enableRotate = !locked; }, [locked]);

  useImperativeHandle(ref, () => ({
    setView: (v) => {
      const { camera, controls } = st.current; const k = Math.max(L, W * 3) * S, h = (H * S) / 2;
      const pos = { ON: [-k * 0.95, h, 0], ARKA: [k * 0.95, h, 0], SAG: [0, h, k * 0.75], SOL: [0, h, -k * 0.75], UST: [0, k * 0.95, 0.001], CAPRAZ: [-k * 0.55, k * 0.55, k * 0.75] }[v];
      camera.position.set(...pos); controls.target.set(0, (H * S) / 3, 0); controls.update();
    },
    snapshot: () => { const { renderer, scene, camera } = st.current; renderer.render(scene, camera); return renderer.domElement.toDataURL("image/png"); },
  }));

  return <div ref={box} data-testid="tir-3d" className="w-full overflow-hidden rounded-lg border border-slate-700" style={{ height }} />;
});

export default Tir3D;
